import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, PageTitle, SkipLink } from '../../components/atoms'
import PointBar from '../../components/userAdmin/PointBar'
import ReskinStudentHeader from '../../components/userAdmin/ReskinStudentHeader'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { ControlHighlight, KeyboardControl } from '../../components/molecules/KeyboardControls'

const videoSrc = '/assets/video/LR_Congrats_Page_Video_DopaGE_032026.mp4'
const bgImageSrc = '/assets/images/lr-congrats-bg.svg'
const blippySrc = '/assets/images/lr-congrats-blippy.png'

export const idCongratsPageMainContent = 'congrats-page-main-content'

export enum FocusableElement {
  ButtonGo = 'button-go',
}

const Congrats: React.FC = () => {
  const navigate = useNavigate()
  const keyboardControls = useKeyboardControls()
  const [videoFading, setVideoFading] = useState(false)
  const [videoRemoved, setVideoRemoved] = useState(false)
  const [pageVisible, setPageVisible] = useState(false)

  useEffect(() => {
    requestAnimationFrame(() => setPageVisible(true))
  }, [])

  const handleVideoEnded = useCallback(() => {
    setVideoFading(true)
  }, [])

  const handleOverlayTransitionEnd = useCallback(() => {
    if (videoFading) {
      setVideoRemoved(true)
    }
  }, [videoFading])

  const handleGo = useCallback(() => {
    navigate('/postsurvey')
  }, [navigate])

  return (
    <div
      className={`font-primary min-h-screen flex flex-col transition-opacity duration-300 ${pageVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{
        backgroundImage: 'radial-gradient(circle, rgba(80,80,80,0.5), #090909)',
        backgroundColor: '#090909',
        overflow: 'visible'
      }}
    >
      <SkipLink targetId={idCongratsPageMainContent} />
      <PageTitle title='Lightning Round Cleared!' />

      {/* Video overlay */}
      {!videoRemoved && (
        <div
          className='fixed inset-0 bg-black'
          style={{
            zIndex: 9999,
            opacity: videoFading ? 0 : 1,
            transition: 'opacity 0.6s ease'
          }}
          onTransitionEnd={handleOverlayTransitionEnd}
          aria-hidden='true'
        >
          <video
            autoPlay
            muted
            playsInline
            onEnded={handleVideoEnded}
            className='w-full h-full object-cover'
          >
            <source src={videoSrc} type='video/mp4' />
          </video>
        </div>
      )}

      {/* Page content (beneath video) */}
      <header className='overflow-visible pt-2'>
        <ReskinStudentHeader>
          <PointBar isCompleted totalExperiencePoints={100} />
        </ReskinStudentHeader>
      </header>

      {/* Background rays */}
      <div
        className='absolute inset-0 pointer-events-none'
        style={{ backgroundImage: `url(${bgImageSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        aria-hidden='true'
      />

      {/* Main content */}
      <main id={idCongratsPageMainContent} tabIndex={-1} className='relative flex flex-col items-center flex-1 z-10'>
        {/* Heading — green-green-black layers matching Figma */}
        <div className='relative w-full' style={{ marginTop: 'clamp(-4px, -0.2vh, 2px)' }}>
          {/* Layer 1: Green halo — expanded green text as border around the black */}
          <div
            className='font-primary font-bold text-center uppercase w-full congrats-green-stroke'
            style={{
              fontSize: 'clamp(50px, 6.5vw, 94px)',
              lineHeight: 1.15,
              color: '#34E89E',
              whiteSpace: 'nowrap',
              position: 'absolute',
              top: 0,
              left: 0
            }}
            aria-hidden='true'
          >
            Lightning Round Cleared!
          </div>
          {/* Layer 2: Green shadow — green text offset 4px (Figma drop shadow) */}
          <div
            className='font-primary font-bold text-center uppercase w-full congrats-green-stroke'
            style={{
              fontSize: 'clamp(50px, 6.5vw, 94px)',
              lineHeight: 1.15,
              color: '#34E89E',
              whiteSpace: 'nowrap',
              position: 'absolute',
              top: 4,
              left: 4
            }}
            aria-hidden='true'
          >
            Lightning Round Cleared!
          </div>
          {/* Layer 3: Black text — dark text on top (Figma fill + border) */}
          <h1
            className='font-primary font-bold text-center uppercase w-full relative'
            style={{
              fontSize: 'clamp(50px, 6.5vw, 94px)',
              lineHeight: 1.15,
              color: '#030303',
              whiteSpace: 'nowrap'
            }}
          >
            Lightning Round Cleared!
          </h1>
        </div>

        {/* Blippy character — overlaps instruction box below */}
        <div className='relative z-10' style={{ marginTop: '-4px', marginBottom: 'clamp(-34px, -2.9vh, -14px)', marginLeft: '-16px' }}>
          <img
            src={blippySrc}
            alt='Blippy character celebrating'
            className='object-contain'
            style={{ width: 'clamp(180px, 37.11vh, 380px)', height: 'clamp(180px, 37.11vh, 380px)' }}
          />
        </div>

        {/* SVG filter for box shadows — matches Figma filter2_dii exactly */}
        <svg width='0' height='0' style={{ position: 'absolute' }} aria-hidden='true'>
          <defs>
            <filter id='purple-box-filter' x='-5%' y='-5%' width='115%' height='120%' colorInterpolationFilters='sRGB'>
              <feFlood floodOpacity='0' result='BackgroundImageFix' />
              <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha' />
              <feOffset dy='10' />
              <feGaussianBlur stdDeviation='4' />
              <feComposite in2='hardAlpha' operator='out' />
              <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.8 0' />
              <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow' />
              <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape' />
              <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha' />
              <feOffset dy='-20' />
              <feGaussianBlur stdDeviation='1' />
              <feComposite in2='hardAlpha' operator='arithmetic' k2='-1' k3='1' />
              <feColorMatrix type='matrix' values='0 0 0 0 0.247059 0 0 0 0 0.0941176 0 0 0 0 0.607843 0 0 0 0.9 0' />
              <feBlend mode='normal' in2='shape' result='effect2_innerShadow' />
              <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha' />
              <feOffset dy='20' />
              <feGaussianBlur stdDeviation='1' />
              <feComposite in2='hardAlpha' operator='arithmetic' k2='-1' k3='1' />
              <feColorMatrix type='matrix' values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.35 0' />
              <feBlend mode='normal' in2='effect2_innerShadow' result='effect3_innerShadow' />
            </filter>
          </defs>
        </svg>

        {/* Instruction box — SVG filter wrapper (sees clipped child for correct shadows) */}
        <div
          style={{
            width: '84.65%',
            filter: 'url(#purple-box-filter)'
          }}
        >
          {/* Inner box — diamond-corner cut, #C77DFF purple */}
          <div
            className='px-[6%] text-center relative flex flex-col items-center justify-center'
            style={{
              minHeight: '272px',
              backgroundColor: '#C77DFF',
              clipPath: 'polygon(28px 0px, calc(100% - 28px) 0px, 100% 28px, 100% calc(100% - 28px), calc(100% - 28px) 100%, 28px 100%, 0 calc(100% - 28px), 0 28px)'
            }}
          >
            <p
              className='relative font-bold text-[#101111]'
              style={{ fontSize: 'clamp(20px, 2.2vw, 36px)', lineHeight: 1.3 }}
            >
              Your upcoming Mission Debrief is the final step to secure your Overdose Prevention &amp; Response Certification.
            </p>
            <p
              className='relative font-bold text-[#101111] mt-[1.5vh]'
              style={{ fontSize: 'clamp(16px, 1.7vw, 26px)', lineHeight: 1.4 }}
            >
              The Debrief will ask you to anonymously log your end-of-mission observations to help Mission Control improve the training for future Rescuers.
            </p>
          </div>
        </div>

        {/* GO button */}
        <div style={{ marginTop: 'clamp(16px, 3.9vh, 40px)', marginBottom: 'clamp(4px, 1vh, 30px)' }}>
          <ControlHighlight
            active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonGo)}
            highlightEffect={HighlightEffect.ButtonHexOutline}
            data-testid={`highlight-${FocusableElement.ButtonGo}`}
          >
            <KeyboardControl>
              <Button
                id={FocusableElement.ButtonGo}
                data-testid={FocusableElement.ButtonGo}
                className='w-[210px] h-[80px] diamond-btn font-bold'
                type='button'
                onClick={handleGo}
              >
                <span className='text-black text-[30px]'>GO</span>
              </Button>
            </KeyboardControl>
          </ControlHighlight>
        </div>
      </main>
    </div>
  )
}

export default Congrats
